/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2026 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <div class="progress-queue-list-wrapper">
    <b-card class="queue-card shadow-sm border-0">
      <div class="queue-header">
        <div>
          <h4 class="queue-title mb-1">Kolejka zadań</h4>
          <div class="queue-subtitle">
            Podgląd aktualnie wykonywanych i zakończonych zadań w aplikacji
          </div>
        </div>

        <div class="queue-actions">
          <b-button
              variant="outline-primary"
              size="sm"
              @click="refreshTasks"
              :disabled="loading"
          >
            <span v-if="!loading">Odśwież</span>
            <span v-else>Odświeżanie...</span>
          </b-button>
        </div>
      </div>

      <b-tabs
          v-model="activeTabIndex"
          content-class="pt-3"
          pills
          lazy
          class="queue-tabs"
      >
        <b-tab>
          <template #title>
            <span class="tab-title">
              W trakcie
              <b-badge variant="primary" pill class="ms-1">
                {{ inProgressTasks.length }}
              </b-badge>
            </span>
          </template>

          <div class="tab-panel">
            <TaskTable
                :key="inProgressTasks"
                :items="inProgressTasks"
                :loading="loading"
                empty-text="Brak zadań w trakcie wykonywania."
                @show-details="openTaskDetails"
            />
          </div>
        </b-tab>

        <b-tab>
          <template #title>
            <span class="tab-title">
              Zakończone sukcesem
              <b-badge variant="success" pill class="ms-1">
                {{ successTasks.length }}
              </b-badge>
            </span>
          </template>

          <div class="tab-panel">
            <TaskTable
                :key="successTasks"
                :items="successTasks"
                :loading="loading"
                empty-text="Brak zadań zakończonych sukcesem."
                @show-details="openTaskDetails"
            />
          </div>
        </b-tab>

        <b-tab>
          <template #title>
            <span class="tab-title">
              Zakończone błędem
              <b-badge variant="danger" pill class="ms-1">
                {{ failedTasks.length }}
              </b-badge>
            </span>
          </template>

          <div class="tab-panel">
            <TaskTable
                :key="failedTasks"
                :items="failedTasks"
                :loading="loading"
                empty-text="Brak zadań zakończonych błędem."
                @show-details="openTaskDetails"
            />
          </div>
        </b-tab>
      </b-tabs>

      <div class="queue-footer">
        <div class="queue-footer-left">
          <span>
            Łącznie rekordów:
            <strong>{{ totalElements }}</strong>
          </span>
        </div>

        <div class="queue-footer-right">
          <b-form-select
              v-model="pageSize"
              :options="pageSizeOptions"
              size="sm"
              class="page-size-select"
              @change="handlePageSizeChange"
          ></b-form-select>

          <b-pagination
              v-model="currentPageUi"
              :total-rows="totalElements"
              :per-page="pageSize"
              first-number
              last-number
              size="sm"
              align="end"
              class="mb-0"
              @update:model-value="handlePageChange"
          ></b-pagination>
        </div>
      </div>
    </b-card>

    <b-modal
        v-model="detailsModalVisible"
        title="Szczegóły zadania"
        size="lg"
        ok-only
        ok-title="Zamknij"
        body-class="task-details-modal-body"
    >
      <div v-if="selectedTask" class="task-details">
        <div class="details-grid">
          <div class="details-item">
            <div class="details-label">Status</div>
            <div class="details-value">
              <b-badge :variant="statusVariant(selectedTask.status)">
                {{ statusLabel(selectedTask.status) }}
              </b-badge>
            </div>
          </div>

          <div class="details-item">
            <div class="details-label">Postęp</div>
            <div class="details-value">
              {{ formatProgress(selectedTask.progress) }}
            </div>
          </div>

          <div class="details-item">
            <div class="details-label">Moment rozpoczęcia</div>
            <div class="details-value">
              {{ formatDateTime(selectedTask.startedAt || selectedTask.queuedAt) }}
            </div>
          </div>

          <div class="details-item">
            <div class="details-label">Moment zakończenia</div>
            <div class="details-value">
              {{ formatDateTime(selectedTask.finishedAt) }}
            </div>
          </div>
        </div>

        <div class="details-section mt-3">
          <div class="details-label">Komunikat</div>
          <div class="details-box">
            {{ selectedTask.message || "Brak komunikatu." }}
          </div>
        </div>

        <div v-if="selectedTask.status === 'FAILED'" class="details-section mt-3">
          <div class="details-label">Szczegóły błędu</div>
          <div class="details-box details-box-error">
            {{ selectedTask.errorMessage || "Brak szczegółów błędu." }}
          </div>
        </div>

        <div class="details-section mt-3">
          <div class="details-label">ID zadania</div>
          <div class="details-box">
            {{ selectedTask.taskId || selectedTask.id || "-" }}
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { useLazyLoaderModule } from "@/state/pinia/lazyloaderManagerModule";
import {useSimplepharmAuthModule} from "@/state/pinia/simplepharmAuthModule";
import {useCommonAppModule} from "@/state/pinia/commonAppModule";
import TaskTable from "@/views/DrugstoreCommonManager/ProgressTaskTable.vue";

export default {
  name: "ProgressQueueList",
  components: {
    TaskTable
  },
  data() {
    return {
      activeTabIndex: 0,
      loading: false,
      currentPage: 0,
      pageSize: 20,
      selectedTask: null,
      detailsModalVisible: false,
      autoRefreshIntervalMs: 2000,
      autoRefreshTimerId: null,
      pageSizeOptions: [
        { value: 10, text: "10 / strona" },
        { value: 20, text: "20 / strona" },
        { value: 50, text: "50 / strona" },
        { value: 100, text: "100 / strona" },
      ],
    };
  },
  computed: {
    simplepharmAuth() {
      return useSimplepharmAuthModule();
    },
    commonModule() {
      return useCommonAppModule();
    },
    lazyloaderModule() {
      return useLazyLoaderModule();
    },
    tasksPage() {
      return this.lazyloaderModule.getTasksPage || {};
    },
    allTasks() {
      return this.tasksPage.content || [];
    },
    totalElements() {
      return this.tasksPage.totalElements || 0;
    },
    currentPageUi() {
      return this.currentPage + 1;
    },
    inProgressTasks() {
      return this.allTasks.filter((task) =>
          ["NEW", "QUEUED", "RUNNING"].includes(task.status)
      );
    },
    successTasks() {
      return this.allTasks.filter((task) => task.status === "DONE");
    },
    failedTasks() {
      return this.allTasks.filter((task) => task.status === "FAILED");
    },
  },
  methods: {
    startAutoRefresh() {
      this.stopAutoRefresh();

      this.autoRefreshTimerId = window.setInterval(() => {
        this.refreshTasks();
      }, this.autoRefreshIntervalMs);
    },

    stopAutoRefresh() {
      if (this.autoRefreshTimerId) {
        window.clearInterval(this.autoRefreshTimerId);
        this.autoRefreshTimerId = null;
      }
    },
    async refreshTasks() {
      if (this.loading) {
        return;
      }

      this.loading = true;

      try {
        await this.lazyloaderModule.httpGetProgressTasksPage({
          page: this.currentPage,
          size: this.pageSize,
        });
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(pageValue) {
      this.currentPage = Math.max(0, pageValue - 1);
      this.refreshTasks();
    },
    handlePageSizeChange() {
      this.currentPage = 0;
      this.refreshTasks();
    },
    openTaskDetails(task) {
      this.selectedTask = task;
      this.detailsModalVisible = true;
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      try {
        return new Date(value).toLocaleString();
      } catch (e) {
        return value;
      }
    },
    formatProgress(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        return "-";
      }

      if (parsed <= 1) {
        return `${Math.round(parsed * 100)}%`;
      }

      return `${Math.round(parsed)}%`;
    },
    statusLabel(status) {
      const labels = {
        NEW: "Nowe",
        QUEUED: "W kolejce",
        RUNNING: "W trakcie",
        DONE: "Zakończono",
        FAILED: "Błąd",
      };
      return labels[status] || status || "-";
    },
    statusVariant(status) {
      if (status === "DONE") return "success";
      if (status === "FAILED") return "danger";
      if (status === "RUNNING" || status === "QUEUED" || status === "NEW") return "primary";
      return "secondary";
    },
  },
  watch: {
    activeTabIndex(newIndex) {
      if (newIndex === 0) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    },
  },
  beforeUnmount() {
    this.stopAutoRefresh();
  },
  created() {

    this.simplepharmAuth.refreshToken().then(() => {
      this.refreshTasks();
      this.startAutoRefresh();
      this.commonModule.httpGetLicensedFeatures();
      this.commonModule.httpGetWorkingProfile();

    });



  },
};
</script>

<style scoped>
.progress-queue-list-wrapper {
  width: 100%;
}

.queue-card {
  border-radius: 14px;
  background: #ffffff;
}

.queue-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}

.queue-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #24324b;
}

.queue-subtitle {
  font-size: 0.92rem;
  color: #6b778c;
}

.queue-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.queue-tabs :deep(.nav-link) {
  border-radius: 10px;
  font-weight: 500;
}

.tab-title {
  display: inline-flex;
  align-items: center;
}

.tab-panel {
  min-height: 280px;
}

.task-table-wrapper {
  border: 1px solid #e8edf3;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}


.queue-footer {
  margin-top: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.queue-footer-left {
  color: #556274;
  font-size: 0.94rem;
}

.queue-footer-right {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.page-size-select {
  width: 140px;
}


.task-details {
  width: 100%;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 14px;
}

.details-item {
  background: #ffffff;
  border: 1px solid #e7ebf1;
  border-radius: 10px;
  padding: 12px 14px;
}

.details-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #6e7b8f;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.details-value {
  color: #23324a;
  font-size: 0.96rem;
}

.details-section {
  width: 100%;
}

.details-box {
  background: #ffffff;
  border: 1px solid #e7ebf1;
  border-radius: 10px;
  padding: 12px 14px;
  color: #23324a;
  word-break: break-word;
  white-space: pre-wrap;
}

.details-box-error {
  border-color: #efc2c2;
  background: #fff5f5;
  color: #892c2c;
}

@media (max-width: 991px) {
  .queue-header {
    flex-direction: column;
    align-items: stretch;
  }

  .queue-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .queue-footer-right {
    justify-content: space-between;
  }

  .page-size-select {
    width: 100%;
    max-width: 220px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>